import styled, { css } from 'styled-components';
import type { HBFieldPlacement, ISpacing, TFieldSize } from './HBField.types';
import HBLabel from '../HBLabel/HBLabel';
import { newTextStyles } from '../HBText/HBText.styles';
import { Props as HBFieldProps } from './HBField';
import { TControlSize } from '../../tokens/types';
import HBText from '../HBText/HBText';
import { vars } from '../../theme';

type NewTextStyleValue = (typeof newTextStyles)[keyof typeof newTextStyles];

function getPlacementSyles(
    placement: HBFieldPlacement,
    spacing: ISpacing,
    size: TFieldSize,
    isDSNext?: boolean
) {
    const { marginBottom, marginLeft, marginRight } = spacing;
    const margin = isDSNext && size === 'lg' ? '12px' : null;

    switch (placement) {
        case 'right':
            return `
				order: 1;
                margin-left: ${margin ?? marginLeft};
			`;

        case 'left':
            return `
                order: 0;
                margin-right: ${margin ?? marginRight};
        `;

        /* top */
        default:
            return `
				margin-bottom: ${margin ?? marginBottom};
			`;
    }
}

/* Label */
const LABEL_STYLES: Record<HBFieldProps['size'], any> = {
    xxs: newTextStyles.body12,
    xs: newTextStyles.body12,
    sm: newTextStyles.body14,
    md: newTextStyles.body14,
    lg: newTextStyles.body16,
    xl: newTextStyles.body16
};

const FONT_SIZE_NEXT: Record<TControlSize, NewTextStyleValue> = {
    sm: newTextStyles.body14,
    md: newTextStyles.body16,
    lg: newTextStyles.body18
};

export interface Props {
    inline: boolean;
    withIcon?: boolean;
    placement: HBFieldPlacement;
    spacing: ISpacing;
    width: string;
    size: TFieldSize;
    isDSNext?: boolean;
}

export const StyledLabel = styled(HBLabel)<Props>`
    ${({ disabled, id }) =>
        !disabled &&
        css`
            cursor: ${id ? 'pointer' : 'default'};
        `};

    ${({ withIcon }) =>
        withIcon &&
        css`
            display: inline-flex;
        `}

    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
        `}

    ${({ inline, spacing }) =>
        inline &&
        css`
            display: inline-block;
            vertical-align: top;
            margin-right: ${spacing.marginRight};
            margin-top: ${spacing.marginTop};
        `};

    ${({ inline, placement, spacing, size, isDSNext }) =>
        !inline &&
        css`
            ${getPlacementSyles(placement, spacing, size, isDSNext)};
        `};

    ${({ width }) =>
        width &&
        css`
            width: ${width};
        `};

    ${({ isDSNext, size }) =>
        !isDSNext &&
        css`
            font-size: ${LABEL_STYLES[size]};
        `};

    ${({ isDSNext, size, disabled }) =>
        isDSNext &&
        css`
            font-size: ${FONT_SIZE_NEXT[size]};
            color: ${disabled
                ? vars.colors.text.disabled
                : vars.colors.text.primary};
        `};
`;

/* Container */
export const Container = styled.div<{
    inline: boolean;
    placement: HBFieldPlacement;
    isDSNext: boolean;
}>`
    display: flex;

    ${({ inline }) =>
        inline &&
        css`
            justify-content: space-between;
            align-items: center;
            width: 100%;
        `}

    ${({ inline, placement }) =>
        !inline &&
        css`
            ${placement === 'top' && 'flex-direction: column;'}
            ${placement !== 'top' && 'align-items: center;'}
        `}

    ${({ isDSNext }) =>
        isDSNext &&
        css`
            align-items: flex-start;
        `}
`;

export const Wrapper = styled.div`
    display: flex;
`;

export const ErrorMessage = styled(HBText)<{ $spacingTop: string }>`
    color: ${vars.colors.text.danger};
    margin-top: ${({ $spacingTop }) => ($spacingTop === 'lg' ? '12px' : '8px')};
`;

export const FieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
`;
